import React, { useState, useEffect, Suspense, lazy } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { DASHBOARD_MAIN_NAV } from '../lib/dashboardNav'
import MetricCard from './analytics/MetricCard'
import ClientDistribution from './analytics/ClientDistribution'
import BudgetAnalysis from './analytics/BudgetAnalysis'
import StatusBreakdown from './analytics/StatusBreakdown'
import StateDistribution from './analytics/StateDistribution'
import PrimaryCalendar from './PrimaryCalendar'
import AgencyDistribution from './analytics/AgencyDistribution'
import BudgetBreakdown from './analytics/BudgetBreakdown'
import ElectionsData from './ElectionsData'
import AdImpactAlertsPanel from './adimpact/AdImpactAlertsPanel'
import ResourcesPage from './resources/ResourcesPage'
import BigPoliticalListPage from '../big-political-list/pages/BigPoliticalListPage'
import IdeaBoardPage from '../idea-board/pages/IdeaBoardPage'

const ClientsDashboardPage = lazy(() => import('./clients/ClientsDashboardPage'))

const DASHBOARD_TAB_NAV = DASHBOARD_MAIN_NAV.filter((n) => !n.href)
const DASHBOARD_TAB_IDS = new Set(DASHBOARD_TAB_NAV.map((n) => n.id))

function Dashboard() {
  const { loading: authLoading, canAccessPage, canWritePage } = useAuth()
  const canEditDashboard = canWritePage('clients')
  const [searchParams, setSearchParams] = useSearchParams()

  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const tabFromUrl = searchParams.get('tab')
  const normalizeTab = (t) => (t === 'dashboard' ? 'clients' : t)

  const [activeTab, setActiveTab] = useState(() => {
    const raw =
      tabFromUrl && DASHBOARD_TAB_IDS.has(normalizeTab(tabFromUrl)) ? normalizeTab(tabFromUrl) : 'clients'
    return raw
  })

  useEffect(() => {
    const t = searchParams.get('tab')
    if (t === 'dashboard') {
      setSearchParams({ tab: 'clients' }, { replace: true })
      return
    }
    if (t && DASHBOARD_TAB_IDS.has(t)) {
      setActiveTab(t)
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (authLoading) return
    if (canAccessPage(activeTab)) return
    const first = DASHBOARD_TAB_NAV.map((n) => n.id).find((id) => canAccessPage(id)) ?? 'clients'
    setActiveTab(first)
    setSearchParams({ tab: first }, { replace: true })
  }, [authLoading, activeTab, canAccessPage, setSearchParams])

  const loadClients = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data, error } = await supabase.from('clients').select('*').order('client_name')
      if (error) throw error
      setClients(data || [])
    } catch (error) {
      console.error('Error loading clients:', error)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'analytics' && activeTab !== 'primary-calendar') return
    loadClients()
  }, [activeTab])

  const getTotalBudget = () =>
    clients.reduce((sum, client) => sum + (client.linear_budget || 0) + (client.streaming_budget || 0), 0)

  const getActiveClientCount = () => clients.filter((client) => client.status === 'Active').length

  const getUniqueStates = () => new Set(clients.filter((c) => c.state).map((c) => c.state)).size

  const getFirewalledCount = () => clients.filter((client) => client.firewalled === true).length

  const getFirewalledPercentage = () => {
    const firewalled = getFirewalledCount()
    return clients.length > 0 ? Math.round((firewalled / clients.length) * 100) : 0
  }

  const getFirewalledBudget = () =>
    clients
      .filter((client) => client.firewalled === true)
      .reduce((sum, client) => sum + (client.linear_budget || 0) + (client.streaming_budget || 0), 0)

  const getAverageFirewalledValue = () => {
    const firewalledClients = clients.filter((client) => client.firewalled === true)
    if (firewalledClients.length === 0) return 0
    const totalFirewalledBudget = firewalledClients.reduce(
      (sum, client) => sum + (client.linear_budget || 0) + (client.streaming_budget || 0),
      0
    )
    return totalFirewalledBudget / firewalledClients.length
  }

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0)

  const analyticsLoading = loading && activeTab === 'analytics'

  return (
    <div
      style={{
        flex: 1,
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-body)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      {activeTab === 'clients' && (
        <Suspense
          fallback={
            <div
              style={{
                padding: '28px 32px',
                color: 'var(--color-text-muted)',
                fontSize: '14px',
              }}
            >
              Loading client dashboard…
            </div>
          }
        >
          <ClientsDashboardPage canEdit={canEditDashboard} />
        </Suspense>
      )}

      {activeTab === 'analytics' && (
        <div style={{ padding: '28px 32px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h1
              style={{
                fontSize: '28px',
                fontWeight: '700',
                margin: '0 0 4px 0',
                color: 'var(--color-text-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              Analytics Dashboard
            </h1>
            <p style={{ color: 'var(--color-text-muted)', margin: '0 0 28px 0', fontSize: '14px' }}>
              Comprehensive insights into client management and performance
            </p>
          </div>

          {analyticsLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '240px',
                color: 'var(--color-text-muted)',
                fontSize: '14px',
              }}
            >
              Loading analytics…
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px',
                }}
              >
                <MetricCard
                  title="Total Clients"
                  value={clients.length}
                  icon={() => (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  )}
                />
                <MetricCard
                  title="Active Clients"
                  value={getActiveClientCount()}
                  icon={() => (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  )}
                  trend={`${Math.round((getActiveClientCount() / clients.length) * 100) || 0}% of total`}
                  trendUp={true}
                />
                <MetricCard
                  title="Total Budget"
                  value={formatCurrency(getTotalBudget())}
                  icon={() => (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  )}
                />
                <MetricCard
                  title="States Covered"
                  value={getUniqueStates()}
                  icon={() => (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  )}
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Firewalled Metrics
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '24px',
                  }}
                >
                  <MetricCard
                    title="Firewalled Clients"
                    value={getFirewalledCount()}
                    icon={() => (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    )}
                  />
                  <MetricCard
                    title="Firewalled %"
                    value={`${getFirewalledPercentage()}%`}
                    icon={() => (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    )}
                    trend={`${getFirewalledCount()} of ${clients.length} total`}
                  />
                  <MetricCard
                    title="Firewalled Budget"
                    value={formatCurrency(getFirewalledBudget())}
                    icon={() => (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                  />
                  <MetricCard
                    title="Avg Firewalled Value"
                    value={formatCurrency(getAverageFirewalledValue())}
                    icon={() => (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    )}
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px',
                }}
              >
                <ClientDistribution clients={clients} />
                <StatusBreakdown clients={clients} />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <AgencyDistribution clients={clients} />
              </div>
              <div style={{ marginBottom: '32px' }}>
                <BudgetBreakdown clients={clients} />
              </div>
              <div style={{ marginBottom: '32px' }}>
                <BudgetAnalysis clients={clients} />
              </div>
              <div style={{ marginBottom: '32px' }}>
                <StateDistribution clients={clients} />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '24px',
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    padding: '24px',
                  }}
                >
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#111827' }}>
                    Average Client Value
                  </h3>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                    {formatCurrency(clients.length > 0 ? getTotalBudget() / clients.length : 0)}
                  </div>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Per client across all tiers</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'elections' && <ElectionsData />}
      {activeTab === 'primary-calendar' && <PrimaryCalendar clients={clients} />}
      {activeTab === 'adimpact-alerts' && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AdImpactAlertsPanel />
        </div>
      )}
      {activeTab === 'big-political-list' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <BigPoliticalListPage />
        </div>
      )}
      {activeTab === 'resources' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ResourcesPage canEditResources={canWritePage('resources')} />
        </div>
      )}
      {activeTab === 'idea-board' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <IdeaBoardPage />
        </div>
      )}
    </div>
  )
}

export default Dashboard
